import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hook to fetch and manage clinic branding data
 * Can be used in patient portal, staff dashboard, emails, etc.
 */
export const useClinicBranding = (clinicId = null) => {
  const [branding, setBranding] = useState({
    name: '',
    logo_url: null,
    primary_color: '#14b8a6',
    secondary_color: '#0d9488'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBranding();
    
    // Listen for branding updates
    const handleBrandingUpdate = () => {
      fetchBranding();
    };
    window.addEventListener('clinic-branding-updated', handleBrandingUpdate);
    
    return () => {
      window.removeEventListener('clinic-branding-updated', handleBrandingUpdate);
    };
  }, [clinicId]);

  const fetchBranding = async () => {
    try {
      setLoading(true);
      
      // If we have a clinicId, fetch public branding (for patient login)
      // Otherwise, fetch authenticated branding
      const endpoint = clinicId 
        ? `${API}/public/clinic/${clinicId}/branding`
        : `${API}/clinic/branding`;
      
      const response = await axios.get(endpoint);
      
      setBranding({
        name: response.data.name || '',
        logo_url: response.data.logo_url || null,
        primary_color: response.data.primary_color || '#14b8a6',
        secondary_color: response.data.secondary_color || '#0d9488'
      });
      setError(null);
    } catch (err) {
      console.error('Failed to fetch branding:', err);
      setError(err.message);
      // Keep default branding on error
    } finally {
      setLoading(false);
    }
  };

  return { branding, loading, error, refetch: fetchBranding };
};

/**
 * Component to display clinic logo with fallback
 */
export const ClinicLogo = ({ 
  logoUrl, 
  clinicName = 'Clinic', 
  size = 'md',
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
    xl: 'h-24 w-24'
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-2xl',
    xl: 'text-4xl'
  };

  if (logoUrl) {
    return (
      <img 
        src={logoUrl} 
        alt={clinicName}
        className={`${sizeClasses[size]} rounded-lg object-contain ${className}`}
      />
    );
  }

  // Fallback to initial letter
  return (
    <div className={`${sizeClasses[size]} rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-bold ${textSizeClasses[size]} shadow-sm ${className}`}>
      {clinicName.charAt(0).toUpperCase()}
    </div>
  );
};

/**
 * Branded header component for patient/staff portals
 */
export const BrandedHeader = ({ 
  branding, 
  portalType = 'Patient',
  user,
  onLogout,
  rightContent = null
}) => {
  return (
    <header className="bg-white border-b sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-3">
            <ClinicLogo 
              logoUrl={branding?.logo_url} 
              clinicName={branding?.name || 'Clinic'} 
              size="md"
            />
            <div>
              <span className="text-xl font-bold text-gray-900">
                {branding?.name || 'MyMedMember'}
              </span>
              <p className="text-xs text-gray-500">{portalType} Portal</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {user && (
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
                <p className="text-xs text-gray-500">{user.role || portalType}</p>
              </div>
            )}
            {rightContent}
            {onLogout && (
              <button 
                onClick={onLogout}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Logout
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default useClinicBranding;
